// Billing conversion analytics — fires platform-correct STANDARD events for
// Meta Pixel (InitiateCheckout / Purchase) and GA4 (begin_checkout / purchase)
// alongside the granular PostHog event, so paid Meta/Google campaigns can
// optimize and attribute against the real subscription conversion (and tell
// plan tiers / billing cycles apart).
//
// IMPORTANT: `value` here is a client-side marketing SIGNAL derived from the
// displayed price — NOT the source of truth for billing. The authoritative
// amount/transaction lives in Stripe (consumed server-side via the
// checkout.session.completed webhook). For high-accuracy, deduplicated
// conversions, add a server-side Conversions API / enhanced-conversions path
// keyed off that webhook.

import { track, trackConnect } from './track'
import { resolveProductHost } from '../product-hosts'

export const BILLING_CURRENCY = 'USD'

export type CheckoutBillingInterval = 'monthly' | 'yearly' | (string & {})

export interface CheckoutPlanContext {
  /** Plan slug, e.g. "starter" | "pro" — the conversion's product id. */
  slug: string
  /** Human-readable plan name for item labels (falls back to slug). */
  name?: string
  /** "monthly" | "yearly". */
  interval: CheckoutBillingInterval
  /** Amount charged now, in BILLING_CURRENCY units (annual total for yearly). */
  value: number
  source?: 'connect'
  entry?: string
  reason?: string
  /**
   * `pricing-default-interval` experiment context, set only on the marketing
   * studios that are in the test. `'unassigned'` means the org entered no arm
   * (flag paused, PostHog unreachable) and is NOT the same as `'control'`.
   */
  pricingDefaultVariant?: string
  /** The interval the arm preselected, before the visitor touched anything. */
  pricingDefaultInterval?: CheckoutBillingInterval
  /** Whether the visitor switched away from that preselection. */
  pricingIntervalSwitched?: boolean
}

/** A query-param bag accepted from either URLSearchParams or Next's readonly one. */
type ParamBag = { get(name: string): string | null }

function conversionProps(
  plan: CheckoutPlanContext,
  transactionId?: string,
): Record<string, unknown> {
  const price = Number.isFinite(plan.value) ? plan.value : undefined
  return {
    // Shared / PostHog
    ...(plan.source
      ? { source: plan.source, entry: plan.entry, reason: plan.reason }
      : {}),
    plan: plan.slug,
    billing_interval: plan.interval,
    ...(plan.pricingDefaultVariant
      ? {
          pricing_default_variant: plan.pricingDefaultVariant,
          pricing_default_interval: plan.pricingDefaultInterval,
          pricing_interval_switched: plan.pricingIntervalSwitched,
        }
      : {}),
    currency: BILLING_CURRENCY,
    value: price,
    ...(transactionId ? { transaction_id: transactionId } : {}),
    // GA4 ecommerce vocabulary
    items: [
      {
        item_id: plan.slug,
        item_name: plan.name ?? plan.slug,
        item_category: plan.interval,
        price,
        quantity: 1,
      },
    ],
    // Meta Pixel vocabulary
    content_type: 'product',
    content_ids: [plan.slug],
    contents: [{ id: plan.slug, quantity: 1, item_price: price }],
    num_items: 1,
  }
}

/** Fire when the user picks a plan and we start a Stripe checkout session. */
export function trackBeginCheckout(plan: CheckoutPlanContext): void {
  const capture = typeof window !== 'undefined' && (plan.source === 'connect' || resolveProductHost(window.location.host).product === 'toolfare') ? trackConnect : track
  capture('begin_checkout', conversionProps(plan), {
    posthogOnly: false,
    metaStandardEvent: 'InitiateCheckout',
  })
}

/**
 * Fire the browser-only checkout-return UX event. The canonical `purchase`
 * conversion is emitted exclusively from the Stripe webhook through the
 * durable delivery ledger, preventing browser/server double counting.
 */
export function trackPurchase(
  plan: CheckoutPlanContext,
  opts?: { transactionId?: string; source?: string },
): void {
  const capture = typeof window !== 'undefined' && (plan.source === 'connect' || resolveProductHost(window.location.host).product === 'toolfare') ? trackConnect : track
  capture('upgrade_succeeded', {
    source: opts?.source,
    ...(plan.source
      ? { source: plan.source, entry: plan.entry, reason: plan.reason }
      : {}),
    plan: plan.slug,
    billing_interval: plan.interval,
    value: Number.isFinite(plan.value) ? plan.value : undefined,
    currency: BILLING_CURRENCY,
    ...(opts?.transactionId ? { transaction_id: opts.transactionId } : {}),
  })
}

/**
 * Append conversion context to a Stripe `success_url` so the return page can
 * fire a plan-aware Purchase. `{CHECKOUT_SESSION_ID}` is a Stripe placeholder
 * substituted with the real session id on redirect — appended raw so its
 * braces are not percent-encoded (which would break substitution).
 */
export function appendCheckoutContext(
  url: string,
  ctx: { plan?: string; interval?: string; value?: number },
): string {
  try {
    const u = new URL(url)
    if (ctx.plan) u.searchParams.set('plan', ctx.plan)
    if (ctx.interval) u.searchParams.set('interval', ctx.interval)
    if (ctx.value != null && Number.isFinite(ctx.value)) {
      u.searchParams.set('value', String(ctx.value))
    }
    const sep = u.search ? '&' : '?'
    return `${u.toString()}${sep}session_id={CHECKOUT_SESSION_ID}`
  } catch {
    return url
  }
}

/**
 * Reconstruct the purchase context from a checkout-return URL. Returns null
 * when no `plan` was carried (e.g. older links), letting callers fall back to
 * the legacy plan-less event.
 */
export function purchaseContextFromParams(
  params: ParamBag,
): { plan: CheckoutPlanContext; transactionId?: string } | null {
  const slug = params.get('plan')
  if (!slug) return null

  const interval = params.get('interval') ?? 'monthly'
  const rawValue = params.get('value')
  const value = rawValue != null ? Number(rawValue) : NaN

  // Stripe substitutes {CHECKOUT_SESSION_ID}; if the placeholder survived
  // unsubstituted (backend did not forward it verbatim), treat it as absent.
  const session = params.get('session_id')
  const transactionId = session && !session.includes('{') ? session : undefined

  return { plan: { slug, interval, value }, transactionId }
}
